import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import type { inferOutput } from '@trpc/tanstack-react-query'
import { Alert, Button, Collapse, Popconfirm, Skeleton, Tabs, Tag, type TabsProps } from 'antd'
import { ArrowLeft, Bot, History, KeyRound, Power, Send, Settings2, ShieldCheck } from 'lucide-react'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { BotBasicInfo } from './bot-basic-info.tsx'
import { BotCredentials } from './bot-credentials.tsx'
import { BotDeleteButton } from './bot-delete-button.tsx'
import { BotDeliveries } from './bot-deliveries.tsx'
import { BotPermissions } from './bot-permissions.tsx'
import { BotRuns } from './bot-runs.tsx'
import { BotTraining } from './bot-training.tsx'
import { BotWecomPush } from './bot-wecom-push.tsx'

export type BotDetailData = inferOutput<ReturnType<typeof useTRPC>['bot']['detail']>
const route = getRouteApi('/_authenticated/system-management/bots/$botId')
export function BotDetail() {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const access = useAccess()
  const { botId } = route.useParams()
  const query = useQuery({
    ...trpc.bot.detail.queryOptions({ id: botId }),
    staleTime: Infinity,
    refetchOnWindowFocus: false
  })
  async function refresh() {
    await Promise.all([
      cache.invalidateQueries(trpc.bot.detail.queryFilter({ id: botId })),
      cache.invalidateQueries(trpc.bot.list.queryFilter())
    ])
  }
  const status = useMutation(trpc.bot.status.mutationOptions({ onSuccess: refresh }))
  const bot = query.data
  return (
    <FormPageContainer
      title="机器人配置"
      breadcrumbRender={false}
      extra={
        <Link to="/system-management/bots" search={{}} className="text-content-muted! flex items-center gap-2 text-sm">
          <ArrowLeft className="size-4" />
          返回列表
        </Link>
      }
    >
      {query.isPending && <Skeleton active />}
      {(query.error || status.error) && (
        <Alert
          type="error"
          title={(query.error ?? status.error)?.message}
          action={<Button onClick={() => void refresh()}>重新载入</Button>}
        />
      )}
      {bot && (
        <div className="space-y-5 pb-6">
          <section aria-label="机器人概览" className="bg-surface border-border overflow-hidden rounded-xl border">
            <div className="flex flex-wrap items-center justify-between gap-4 p-5 sm:p-6">
              <div className="flex min-w-0 items-center gap-4">
                <div className="bg-accent/10 text-accent flex size-12 shrink-0 items-center justify-center rounded-xl">
                  <Bot className="size-6" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-content m-0 text-xl font-semibold break-all">{bot.name}</h1>
                    <Tag className="m-0!" color={bot.isEnabled ? 'success' : 'default'}>
                      {bot.isEnabled ? '已启用' : '已停用'}
                    </Tag>
                  </div>
                  <p className="text-content-muted mt-1.5 mb-0 text-sm">
                    {bot.kind === 'customer' ? '客户客服 · 内部群 / 外部群' : '内部助手 · 仅内部群'}
                    <span className="text-border-strong mx-2">/</span>
                    企业微信机器人
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <BotDeleteButton
                  bot={bot}
                  onDeleted={() => cache.invalidateQueries(trpc.bot.list.queryFilter())}
                  returnToList
                />
                {access.can('bot.status.update') && (
                  <Popconfirm
                    title={bot.isEnabled ? '停用机器人并使现有授权失效？' : '启用机器人？'}
                    onConfirm={() =>
                      status.mutateAsync({ id: bot.id, expectedVersion: bot.version, isEnabled: !bot.isEnabled })
                    }
                  >
                    <Button danger={bot.isEnabled} loading={status.isPending} icon={<Power className="size-4" />}>
                      {bot.isEnabled ? '停用机器人' : '启用机器人'}
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </div>
          </section>
          <BotTabs bot={bot} onSaved={refresh} />
        </div>
      )}
    </FormPageContainer>
  )
}
function BotTabs({ bot, onSaved }: { bot: BotDetailData; onSaved: () => Promise<void> }) {
  const access = useAccess()
  const { tab } = route.useSearch()
  const navigate = route.useNavigate()
  const items: TabsProps['items'] = [
    {
      key: 'basic',
      label: '基本信息',
      icon: <Settings2 className="inline size-4 align-[-0.125rem]" />,
      children: <BotBasicInfo key={bot.id} bot={bot} onSaved={onSaved} />
    }
  ]
  items.push({
    key: 'permissions',
    label: '业务权限',
    icon: <ShieldCheck className="inline size-4 align-[-0.125rem]" />,
    children: <BotPermissions key={`${bot.id}:${bot.version}`} bot={bot} onSaved={onSaved} />
  })
  if (access.can('bot.credential.update') || (bot.kind === 'customer' && access.can('bot.training.update'))) {
    items.push({
      key: 'credentials',
      label: 'MCP 接入',
      icon: <KeyRound className="inline size-4 align-[-0.125rem]" />,
      children: <BotAdvanced bot={bot} onSaved={onSaved} />
    })
  }
  if (access.can('bill.group.read')) {
    items.push({
      key: 'runs',
      label: '发送记录',
      icon: <History className="inline size-4 align-[-0.125rem]" />,
      destroyOnHidden: true,
      children: <BotRecords botId={bot.id} />
    })
  }
  if (bot.kind === 'customer') {
    items.push({
      key: 'push',
      label: '企微推送',
      icon: <Send className="inline size-4 align-[-0.125rem]" />,
      destroyOnHidden: true,
      children: <BotWecomPush key={bot.id} botId={bot.id} />
    })
  }
  const requestedTab = tab === 'training' ? 'credentials' : tab
  const activeTab = items.some((item) => item.key === requestedTab) ? requestedTab : 'basic'
  return (
    <Tabs
      classNames={{ header: 'mb-5!', content: 'min-w-0' }}
      activeKey={activeTab}
      onChange={(value) =>
        void navigate({ search: (previous) => ({ ...previous, tab: value as typeof tab }), replace: true })
      }
      items={items}
    />
  )
}

function BotRecords({ botId }: { botId: string }) {
  const { recordType } = route.useSearch()
  const navigate = route.useNavigate()
  return (
    <Tabs
      size="small"
      classNames={{ header: 'mb-3!', content: 'min-w-0' }}
      activeKey={recordType ?? 'push'}
      onChange={(value) =>
        void navigate({ search: (previous) => ({ ...previous, recordType: value as 'push' | 'collection' }) })
      }
      items={[
        { key: 'push', label: '主动发送', destroyOnHidden: true, children: <BotDeliveries botId={botId} /> },
        { key: 'collection', label: '自助出单', children: <BotRuns botId={botId} /> }
      ]}
    />
  )
}

function BotAdvanced({ bot, onSaved }: { bot: BotDetailData; onSaved: () => Promise<void> }) {
  const access = useAccess()
  const { tab } = route.useSearch()
  return (
    <div className="space-y-5">
      {access.can('bot.credential.update') && <BotCredentials key={bot.id} bot={bot} />}
      {bot.kind === 'customer' && access.can('bot.training.update') && (
        <Collapse
          defaultActiveKey={tab === 'training' ? ['training'] : []}
          items={[
            {
              key: 'training',
              label: '管理员演练（高级）',
              children: (
                <div className="space-y-4">
                  <Alert
                    type="info"
                    showIcon
                    title="用于测试客户自助出单流程，日常手动发送账单无需配置。"
                    description="创建临时授权后，管理员可验证机器人权限和出单结果。默认演练不会创建正式账单或发送消息；此处不编辑机器人的提示词或话术。"
                  />
                  <BotTraining key={`${bot.id}:${bot.version}`} bot={bot} onSaved={onSaved} />
                </div>
              )
            }
          ]}
        />
      )}
    </div>
  )
}
